import React, { useState, useCallback } from 'react';
import { usePersonSearchQuery } from '../../services/graphql/api';

/**
 * Custom hook for student search functionality
 * Extracts all search logic from StudentSearchInterface
 */
export const useStudentSearch = ({
  onStudentSelect,
  selectedStudent,
  searchQuery = '',
  searchResults = [],
  hasSearched = false,
  onSearchStateChange
}) => {
  // Local state
  const [searchError, setSearchError] = useState(null);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // GraphQL query
  const {
    data: searchData,
    isLoading: isApiLoading,
    error: apiError,
    refetch: triggerSearch,
    isFetching
  } = usePersonSearchQuery(debouncedSearchQuery.trim(), {
    skip: !debouncedSearchQuery.trim() || debouncedSearchQuery.trim().length < 2
  });

  // Combined loading state
  const showLoadingIndicator = isApiLoading || isFetching;

  // Transform GraphQL results
  const transformSearchResults = useCallback((results) => {
    if (!results || !Array.isArray(results)) return [];
    
    return results.map(person => ({
      id: person.applicantEmployeeId,
      name: `${person.firstName} ${person.lastName}`.trim(),
      email: person.email || 'No email available',
      programs: person.programId ? [person.programId] : [],
      term: person.term,
      firstName: person.firstName,
      lastName: person.lastName,
      applicantEmployeeId: person.applicantEmployeeId,
      programId: person.programId
    }));
  }, []);

  // Handle search input change
  const handleSearchChange = useCallback((event) => {
    const query = event.target.value;
    
    if (searchError) {
      setSearchError(null);
    }
    
    if (onSearchStateChange) {
      onSearchStateChange(query, searchResults, hasSearched);
    }
  }, [searchError, onSearchStateChange, searchResults, hasSearched]);

  // Handle student selection
  const handleStudentSelect = useCallback((student) => {
    if (onStudentSelect) {
      onStudentSelect(student.id, student);
    }
  }, [onStudentSelect]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event, student) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleStudentSelect(student);
    }
  }, [handleStudentSelect]);

  // Handle clear search
  const handleClearSearch = useCallback(() => {
    if (onSearchStateChange) {
      onSearchStateChange('', [], false);
    }
    // Clear selected student as well
    if (onStudentSelect) {
      onStudentSelect(null, null);
    }
    setSearchError(null);
    setDebouncedSearchQuery('');
  }, [onSearchStateChange, onStudentSelect]);

  // Update search results when GraphQL data changes
  React.useEffect(() => {
    if (searchQuery.trim().length < 2) {
      return;
    }
    
    if (searchData?.results) {
      const transformedResults = transformSearchResults(searchData.results);
      if (onSearchStateChange) {
        onSearchStateChange(searchQuery, transformedResults, true);
      }
    } else if (apiError) {
      setSearchError(apiError?.message || 'Failed to search students. Please try again.');
      if (onSearchStateChange) {
        onSearchStateChange(searchQuery, [], hasSearched);
      }
    }
  }, [searchData, apiError, transformSearchResults, searchQuery, hasSearched, onSearchStateChange]);

  // Debounce the search query
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Clear previous results when search query changes significantly
  React.useEffect(() => {
    if (hasSearched && searchResults.length > 0) {
      if (onSearchStateChange) {
        onSearchStateChange(searchQuery, [], false);
      }
    }
  }, [searchQuery]);

  // Return organized props for components
  return {
    // Input props
    inputProps: {
      searchQuery,
      showLoadingIndicator,
      onChange: handleSearchChange,
      onClear: handleClearSearch,
      disabled: false
    },

    // Status props
    statusProps: {
      showLoadingIndicator,
      searchError,
      isSearching: showLoadingIndicator
    },

    // Selected student props
    selectedProps: {
      selectedStudent,
      hasSearched,
      searchQuery
    },

    // Results props
    resultsProps: {
      hasSearched,
      searchResults,
      selectedStudent,
      showLoadingIndicator,
      debouncedSearchQuery,
      onStudentSelect: handleStudentSelect,
      onKeyDown: handleKeyDown
    },

    // Raw state for debugging
    state: {
      searchQuery,
      searchResults,
      hasSearched,
      showLoadingIndicator,
      searchError,
      debouncedSearchQuery
    }
  };
};

export default useStudentSearch;